import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, CreditCard, FileText, Download, Printer } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const paymentId = searchParams.get("payment_id");
  const invoiceId = searchParams.get("invoice_id");
  const processorParam = searchParams.get("processor");
  const statusParam = searchParams.get("status");
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    if (statusParam === "failed") { setStatus("error"); return; }

    const isFiserv = processorParam === "fiserv" && paymentId;
    const id = isFiserv ? paymentId : sessionId;
    if (!id) { setStatus("error"); return; }

    let attempts = 0;
    const maxAttempts = 12;

    const poll = async () => {
      try {
        const endpoint = isFiserv
          ? `/payments/fiserv-status/${id}`
          : `/payments/status/${id}`;
        const res = await apiClient.get(endpoint);
        setTx(res.data);

        if (res.data.payment_status === "paid") {
          // Fetch full receipt with invoice
          try {
            const receiptRes = await apiClient.get(`/payments/receipt/${res.data.id || id}`);
            setTx(receiptRes.data);
          } catch {}
          setStatus("paid");
          return;
        } else if (["expired", "failed"].includes(res.data.payment_status)) {
          setStatus("error");
          return;
        }
      } catch {}
      attempts++;
      if (attempts < maxAttempts) setTimeout(poll, 2500);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, paymentId, processorParam, statusParam, invoiceId]);

  const maskCard = (num) => {
    if (!num) return null;
    const last4 = num.replace(/\D/g, "").slice(-4);
    return last4 ? `**** **** **** ${last4}` : null;
  };

  const getCardBrand = (brand) => {
    if (!brand) return "";
    const b = brand.toUpperCase();
    if (b.includes("VISA")) return "VISA";
    if (b.includes("MASTER")) return "MASTERCARD";
    if (b.includes("AMEX")) return "AMEX";
    if (b.includes("DISCOVER")) return "DISCOVER";
    return b;
  };

  const handlePrint = () => window.print();

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-success-page">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto text-sag-navy animate-spin mb-6" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
            <p className="text-slate-500">Please wait while we confirm your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error" || status === "timeout") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-success-page">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-10 w-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {status === "timeout" ? "Payment Processing" : "Payment Failed"}
            </h2>
            <p className="text-slate-500 mb-6">
              {status === "timeout"
                ? "Your payment is taking longer than expected. Check your email for confirmation."
                : "Your payment could not be processed. Please try again or contact support."}
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/client")} className="w-full bg-sag-navy hover:bg-sag-navy-800">
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // PCI-COMPLIANT RECEIPT
  const isPaid = tx?.payment_status === "paid";
  const isFiserv = tx?.processor === "fiserv";
  const isStripe = tx?.processor === "stripe";
  const paidAt = tx?.paid_at || tx?.updated_at;
  const items = tx?.invoice?.items || [{ description: tx?.service_name || "Service", quantity: 1, unit_price: tx?.amount, total: tx?.amount }];

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 print:bg-white print:p-0" data-testid="payment-success-page">
      <div className="max-w-xl w-full space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between print:hidden">
          <Button variant="ghost" size="sm" onClick={() => navigate("/client")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" /> Print Receipt
          </Button>
        </div>

        <Card className="shadow-xl border-0 overflow-hidden" data-testid="payment-receipt">
          {/* Status Banner */}
          <div className={`py-6 px-6 text-center ${isPaid ? "bg-emerald-600" : "bg-red-600"}`}>
            <div className="flex justify-center mb-3">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                {isPaid ? <CheckCircle2 className="h-8 w-8 text-white" /> : <XCircle className="h-8 w-8 text-white" />}
              </div>
            </div>
            <h1 className="text-white text-xl font-bold">{isPaid ? "Payment Successful" : "Payment Failed"}</h1>
            <p className="text-3xl font-bold text-white mt-1">${tx?.amount?.toFixed(2)}</p>
            <p className="text-white/80 text-sm mt-1">
              {paidAt ? new Date(paidAt).toLocaleString("en-US", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
            </p>
          </div>

          <CardContent className="p-6 space-y-5">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400 text-xs uppercase font-medium">Order ID</p>
                <p className="font-mono font-semibold text-slate-900">{tx?.id ? `ORD-${tx.id.slice(0, 10)}` : "—"}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase font-medium">Date & Time</p>
                <p className="font-semibold text-slate-900">
                  {paidAt ? new Date(paidAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </p>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase font-medium">Customer</p>
                <p className="font-semibold text-slate-900">{tx?.client_name || "—"}</p>
                <p className="text-xs text-slate-500">{tx?.client_email || ""}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase font-medium">Amount</p>
                <p className="font-bold text-2xl text-slate-900">${tx?.amount?.toFixed(2)}</p>
              </div>
            </div>

            <Separator />

            {/* Line Items */}
            <div>
              <p className="text-slate-400 text-xs uppercase font-medium mb-3">Items</p>
              {items.map((item, i) => (
                <div key={`item-${item.description}-${i}`} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{item.description}</p>
                    <p className="text-xs text-slate-500">Qty: {item.quantity || 1}</p>
                  </div>
                  <p className="font-semibold text-slate-900">${(item.total || item.unit_price || 0).toFixed(2)}</p>
                </div>
              ))}
              <div className="flex items-center justify-between pt-3">
                <p className="font-bold text-slate-900">Total</p>
                <p className="font-bold text-xl text-emerald-700">${tx?.amount?.toFixed(2)}</p>
              </div>
            </div>

            <Separator />

            {/* Payment Transaction Details */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-5" data-testid="transaction-details">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-sky-700" />
                <h3 className="font-bold text-slate-900">Payment Transaction Details</h3>
              </div>

              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                {/* Status */}
                <div>
                  <p className="text-slate-400 text-xs uppercase font-medium">
                    {isFiserv ? "Fiserv Status" : "Status"}
                  </p>
                  <p className={`font-bold text-lg ${isPaid ? "text-emerald-600" : "text-red-600"}`}>
                    {isFiserv ? (tx?.fiserv_status || "APPROVED") : (isPaid ? "COMPLETED" : "FAILED")}
                  </p>
                </div>

                {/* Approval Code */}
                {(tx?.fiserv_approval_code || tx?.stripe_payment_intent) && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase font-medium">Approval Code</p>
                    <p className="font-mono font-bold text-slate-900 text-sm break-all">
                      {tx?.fiserv_approval_code || tx?.stripe_payment_intent || "—"}
                    </p>
                  </div>
                )}

                {/* Card */}
                {(tx?.fiserv_card_number || isStripe) && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase font-medium">Card</p>
                    <p className="font-mono font-bold text-slate-900">
                      {maskCard(tx?.fiserv_card_number) || "**** **** **** ****"}
                    </p>
                    {tx?.fiserv_card_brand && (
                      <p className="text-xs text-slate-600 font-semibold">({getCardBrand(tx.fiserv_card_brand)})</p>
                    )}
                  </div>
                )}

                {/* Transaction ID */}
                <div>
                  <p className="text-slate-400 text-xs uppercase font-medium">Transaction ID</p>
                  <p className="font-mono font-semibold text-slate-900 text-sm">
                    {tx?.fiserv_txn_id || tx?.session_id || tx?.id?.slice(0, 16) || "—"}
                  </p>
                </div>

                {/* Reference */}
                <div>
                  <p className="text-slate-400 text-xs uppercase font-medium">Reference #</p>
                  <p className="font-mono font-semibold text-slate-900 text-sm">
                    {tx?.fiserv_ref_number || tx?.id?.slice(0, 16) || "—"}
                  </p>
                </div>

                {/* Payment Method */}
                <div>
                  <p className="text-slate-400 text-xs uppercase font-medium">Payment Method</p>
                  <p className="font-semibold text-slate-900">
                    {isFiserv ? "Fiserv (Scotiabank)" : isStripe ? "Stripe" : tx?.processor || "Card"}
                  </p>
                </div>

                {/* Paid At */}
                <div>
                  <p className="text-slate-400 text-xs uppercase font-medium">Paid At</p>
                  <p className="font-semibold text-slate-900 text-sm">
                    {paidAt ? new Date(paidAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
                  </p>
                </div>

                {/* Fiserv Processed */}
                {tx?.fiserv_txndatetime && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase font-medium">Fiserv Processed</p>
                    <p className="font-mono font-semibold text-slate-900 text-sm">{tx.fiserv_txndatetime}</p>
                  </div>
                )}

                {/* Processor Response */}
                {tx?.fiserv_processor_response && (
                  <div>
                    <p className="text-slate-400 text-xs uppercase font-medium">Processor Response</p>
                    <p className="font-mono font-semibold text-slate-900">{tx.fiserv_processor_response}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Invoice Reference */}
            {tx?.invoice && (
              <>
                <Separator />
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span className="text-slate-500">Invoice</span>
                  </div>
                  <span className="font-mono font-semibold">{tx.invoice.invoice_number}</span>
                </div>
              </>
            )}

            {/* Merchant */}
            <Separator />
            <div className="text-center text-xs text-slate-400 space-y-1">
              <p className="font-semibold text-slate-500">Sebastian Alliance Group Ltd.</p>
              <p>Nassau, Bahamas</p>
              <p>helpdesk.sebasalliance.com</p>
              <p className="mt-2">This is your official payment receipt. Please retain for your records.</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2 print:hidden">
              {tx?.ticket_id && (
                <Button onClick={() => navigate(`/tickets/${tx.ticket_id}`)} className="flex-1 bg-sag-navy hover:bg-sag-navy-800" data-testid="go-to-ticket">
                  View Ticket
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/client")} className="flex-1" data-testid="go-to-dashboard">
                Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
